import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { useNodesState, useEdgesState, Node, Edge } from '@xyflow/react';
import { dialCodeToCountryCode } from '../../utils/countryCodeHelpers';

import { createAgenticWorkflow, updateAgenticWorkflow } from 'services/api/WorkflowServiceAPI';

import 'react-phone-input-2/lib/style.css';

import styles from './AgenticWorkflowBuilder.module.scss';
import NotificationModal from 'components/NotificationModal';
import { Button, Modal } from 'common/components';
import {
    NodeData,
    DecisionNodeData,
    WorkflowDefinition,
    WorkflowData,
    WorkflowTagsType,
    OperatingHoursSettings,
    WorkflowConfig,
} from './WorkflowBuilder.types';
import { WorkflowActions, WorkflowCanvas, WorkflowConfigs } from './components';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetEditAgenticWorkflow } from 'services/hooks/queries';
import {
    formatGenericTag,
    toESTIsoString,
    parseGenericTags,
    parseGenericPrompts,
    DEFAULT_GENERIC_PROMPTS,
    findHeadNodeId,
    convertToServerNodes,
    validateWorkflowConnections,
} from './workflowNodeUtils';

export interface WorkflowConfigFormData {
    flowName: string;
    flowDescription: string;
    workflowStart: Date | null;
    workflowEnd: Date | null;
    countryCode: string;
    dialCode: string;
    isGenericConfigured: boolean;
    workflowTagsType: WorkflowTagsType;
    genericTags: { value: string; type: string; order?: number }[];
    genericPrompts: Record<string, { promptName: string; value: string }>;
    callTestTranscript?: string;
    isHumanTransfer: boolean;
    operatingHoursEnabled: boolean;
    operatingHoursStartTime: string;
    operatingHoursEndTime: string;
    operatingHoursTimezone: string;
}

function parseOperatingHoursFormData(
    oh?: OperatingHoursSettings
): Partial<
    Pick<
        WorkflowConfigFormData,
        'operatingHoursEnabled' | 'operatingHoursStartTime' | 'operatingHoursEndTime' | 'operatingHoursTimezone'
    >
> {
    if (!oh) return {};
    const is24h = oh.startTime === '00:00' && oh.endTime === '23:59';
    return {
        operatingHoursEnabled: is24h,
        ...(is24h ? {} : { operatingHoursStartTime: oh.startTime, operatingHoursEndTime: oh.endTime }),
        operatingHoursTimezone: oh.timezone,
    };
}

const AgenticWorkflowBuilder: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isDuplicate = (location.state as { duplicate?: boolean } | null)?.duplicate;

    const [isSavingWorkflow, setIsSavingWorkflow] = useState<boolean>(false);

    // Add state for NotificationModal
    const [notificationModalOpen, setNotificationModalOpen] = useState<boolean>(false);
    const [notificationMessage, setNotificationMessage] = useState<string>('');
    const [notificationType, setNotificationType] = useState<'success' | 'error' | 'warning' | 'info'>('success');
    const [notificationTitle, setNotificationTitle] = useState<string>('');
    const [notificationCallback, setNotificationCallback] = useState<(() => () => void) | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const counterRef = useRef<number>(100);

    const [nodes, setNodes] = useNodesState<Node<NodeData>>([]);
    const [edges, setEdges] = useEdgesState<Edge<NodeData>>([]);
    const [terminalDecisionNodeId, setTerminalDecisionNodeId] = useState<string | null>(null);

    const isNewWorkflow = useMemo(() => location.pathname.endsWith('/new'), [location.pathname]);
    const isEditWorkflow = useMemo(
        () => !!(!isNewWorkflow && id && location.pathname.includes('/edit')),
        [location.pathname, id]
    );

    const form = useForm<WorkflowConfigFormData>({
        defaultValues: {
            flowName: 'New Workflow',
            flowDescription: '',
            workflowStart: null,
            workflowEnd: null,
            countryCode: 'us',
            dialCode: '+1',
            isGenericConfigured: false,
            workflowTagsType: WorkflowTagsType.PREDEFINED,
            genericTags: [{ value: '', type: 'ternary', order: 0 }],
            genericPrompts: DEFAULT_GENERIC_PROMPTS,
            callTestTranscript: '',
            isHumanTransfer: true,
            operatingHoursEnabled: true,
            operatingHoursStartTime: '09:00',
            operatingHoursEndTime: '17:00',
            operatingHoursTimezone: 'America/New_York',
        },
    });

    const workflowTitle = form.watch('flowName');

    const { editAgenticWorkflow: workflowData, isLoadingEditAgenticWorkflow } = useGetEditAgenticWorkflow({
        id: id || '',
        isEnabled: !!(id && location.pathname.includes('/edit')),
    });

    // Load existing workflow if editing, or initialize new workflow
    useEffect(() => {
        const initializeWorkflow = async (): Promise<void> => {
            if (!isNewWorkflow && id && location.pathname.includes('/edit')) {
                const title = isDuplicate ? `${workflowData?.title} (Duplicate)` : workflowData?.title;
                // Prepare form data object with all values
                const formData: WorkflowConfigFormData = {
                    flowName: title || '',
                    flowDescription: workflowData?.description || '',
                    workflowStart: workflowData?.start_date ? parseISO(workflowData.start_date) : null,
                    workflowEnd: workflowData?.end_date ? parseISO(workflowData.end_date) : null,
                    dialCode: workflowData?.country_code || '+1',
                    countryCode: workflowData?.country_code ? dialCodeToCountryCode(workflowData.country_code) : 'us',
                    workflowTagsType: workflowData?.tag_type || WorkflowTagsType.PREDEFINED,
                    isGenericConfigured: workflowData?.tag_type === WorkflowTagsType.GENERIC,
                    genericTags: (() => {
                        const arr = parseGenericTags(workflowData?.workflow_definition?.generic_tags);
                        return arr.length > 0 ? arr : [{ value: '', type: 'ternary', order: 0 }];
                    })(),
                    genericPrompts: parseGenericPrompts(
                        workflowData?.workflow_definition?.generic_prompts,
                        workflowData?.workflow_definition?.generic_tags
                    ),
                    isHumanTransfer:
                        typeof workflowData?.human_transfer === 'boolean' ? workflowData.human_transfer : true,
                    operatingHoursEnabled: true,
                    operatingHoursStartTime: '09:00',
                    operatingHoursEndTime: '17:00',
                    operatingHoursTimezone: 'America/New_York',
                };

                const responseConfig = workflowData?.config as unknown as WorkflowConfig | undefined;
                const existingOH = responseConfig?.operatingHours;
                const ohFields = parseOperatingHoursFormData(existingOH);

                form.reset({ ...formData, ...ohFields });

                setTerminalDecisionNodeId(workflowData?.terminal_decision_node_id ?? null);

                // Update the counter to prevent ID conflicts if nodes exist
                if (workflowData?.workflow_definition?.nodes?.length) {
                    const nodeIds = workflowData.workflow_definition.nodes.map((node) => {
                        const idNum = parseInt(node.id.replace('node_', ''));
                        return isNaN(idNum) ? 0 : idNum;
                    });

                    if (nodeIds.length > 0) {
                        const maxNodeId = Math.max(...nodeIds);
                        counterRef.current = maxNodeId + 1;
                    }
                }
            }
        };

        initializeWorkflow();
    }, [id, location.pathname, workflowData, isNewWorkflow, isDuplicate]);

    useEffect(() => {
        // Do not validate while nodes are still loading — empty `nodes` would clear a valid API id.
        if (nodes.length === 0) return;
        const decisionIds = new Set(nodes.filter((n) => n.type === 'decisionNode').map((n) => n.id));
        if (terminalDecisionNodeId && !decisionIds.has(terminalDecisionNodeId)) {
            setTerminalDecisionNodeId(null);
        }
    }, [nodes, terminalDecisionNodeId]);

    const handleCreateTerminalDecisionNode = (): void => {
        const newId = `node_${counterRef.current + 1}`;
        counterRef.current += 1;
        const removeTerminalNode = (nodeId: string, _e?: React.MouseEvent) => {
            setNodes((nds) => nds.filter((n) => n.id !== nodeId));
            setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
            setTerminalDecisionNodeId((prev) => (prev === nodeId ? null : prev));
        };
        const data: DecisionNodeData = {
            label: 'Terminal transitions',
            subLabel: 'No conditions',
            onDelete: removeTerminalNode,
            conditions: [],
            defaultNextNode: null,
        };
        const newNode: Node<NodeData> = {
            id: newId,
            type: 'decisionNode',
            position: { x: 250, y: 200 },
            data,
        };
        setNodes((nds) => nds.concat(newNode));
        setTerminalDecisionNodeId(newId);
    };

    // Function to show notification modal
    const showNotification = (
        message: string,
        title = '',
        type: 'success' | 'error' | 'warning' | 'info' = 'success',
        callback: (() => () => void) | null = null
    ): void => {
        setNotificationMessage(message);
        setNotificationType(type);
        setNotificationTitle(title || (type === 'success' ? 'Success' : 'Error'));
        setNotificationCallback(() => callback);
        setNotificationModalOpen(true);
    };

    // Function to close notification modal
    const closeNotification = (): void => {
        setNotificationModalOpen(false);
        if (notificationCallback) {
            // Execute the callback function which returns another function
            const callbackFn = notificationCallback();
            if (typeof callbackFn === 'function') {
                callbackFn();
            }
            setNotificationCallback(null);
        }
    };

    const handleSave = async (data: WorkflowConfigFormData): Promise<void> => {
        setIsSavingWorkflow(true);

        if (nodes.length === 0) {
            showNotification('Please add at least one node to your workflow', 'Validation Error', 'error');
            setIsSavingWorkflow(false);
            return;
        }

        // Validate workflow connections
        const validation = validateWorkflowConnections(nodes, edges);
        if (!validation.valid) {
            const warningMessage = `Warning: The following issues were found in your workflow:\n\n${validation.issues.join('\n')}\n\nDo you want to save anyway?`;
            if (!window.confirm(warningMessage)) {
                setIsSavingWorkflow(false);
                return;
            }
        }

        await saveWorkflow(data);
        setIsSavingWorkflow(false);
    };

    // Create a saveWorkflow function
    const saveWorkflow = async (formData: WorkflowConfigFormData): Promise<void> => {
        // Find the head node
        const headNodeId = findHeadNodeId(
            nodes,
            edges,
            terminalDecisionNodeId ?? workflowData?.terminal_decision_node_id ?? null
        );
        if (!headNodeId) {
            showNotification(
                'Could not determine the starting node. Please ensure your workflow has a valid structure.',
                'Error',
                'error'
            );
            return;
        }

        // Find deleted node IDs by comparing original with current nodes (use query data at save time)
        let deletedNodeIds: string[] | null = null;
        const originalNodeIds = workflowData?.workflow_definition?.nodes?.map((node) => node.id) ?? [];
        if (!isNewWorkflow && !isDuplicate && originalNodeIds.length > 0) {
            const currentNodeIds = nodes.map((node: Node<NodeData>) => node.id);
            const deletedNodes = originalNodeIds.filter((id) => !currentNodeIds.includes(id));
            if (deletedNodes.length > 0) {
                deletedNodeIds = deletedNodes;
            }
        }

        // Convert nodes to server format
        const serverNodes = convertToServerNodes(nodes, edges);

        // Build the final payload (use workflow_definition from query at save time to avoid extra state/rerender)
        const originalDefinition = workflowData?.workflow_definition;
        const baseConfig: WorkflowDefinition =
            isNewWorkflow || !originalDefinition || isDuplicate
                ? { nodes: serverNodes }
                : formData.isGenericConfigured
                  ? { ...originalDefinition, nodes: serverNodes }
                  : (() => {
                        const { generic_tags, generic_prompts, ...rest } = originalDefinition;
                        return { ...rest, nodes: serverNodes };
                    })();

        if (formData.isGenericConfigured) {
            const { genericTags, genericPrompts } = formData;
            const sortedTags = [...genericTags].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            baseConfig.generic_tags = Object.fromEntries(
                sortedTags.map((tag, index) => {
                    const formattedTag = formatGenericTag(tag.value);
                    return [
                        formattedTag,
                        {
                            type: tag.type,
                            prompt: genericPrompts[formattedTag]?.value ?? '',
                            order: index,
                        },
                    ];
                })
            );
            baseConfig.generic_prompts = {
                general_prompt: genericPrompts.general_prompt.value,
                sanity_check_prompt: genericPrompts.sanity_check_prompt?.value ?? '',
                unqualify_prompt: genericPrompts.unqualify_prompt.value,
                qualify_prompt: genericPrompts.qualify_prompt.value,
                undetermined_prompt: genericPrompts.undetermined_prompt.value,
            };
        }

        // workflowTagsType can be missing from formData when the Select is disabled (isGenericConfigured);
        // RHF excludes disabled fields from submit data
        const tag_type = formData.workflowTagsType ?? form.getValues('workflowTagsType');

        const operatingHours: OperatingHoursSettings = formData.operatingHoursEnabled
            ? { startTime: '00:00', endTime: '23:59', timezone: formData.operatingHoursTimezone }
            : {
                  startTime: formData.operatingHoursStartTime,
                  endTime: formData.operatingHoursEndTime,
                  timezone: formData.operatingHoursTimezone,
              };

        const workflowPayload: WorkflowData = {
            title: formData.flowName,
            description: formData.flowDescription,
            start_date: formData.workflowStart ? toESTIsoString(formData.workflowStart) : null,
            end_date: formData.workflowEnd ? toESTIsoString(formData.workflowEnd) : null,
            head_node_id: headNodeId,
            terminal_decision_node_id: terminalDecisionNodeId || null,
            deleted_node_ids: deletedNodeIds,
            config: baseConfig,
            tag_type,
            human_transfer: formData.isHumanTransfer,
            operatingHours,
        };

        try {
            if (!isNewWorkflow && !id) {
                showNotification('Cannot update workflow: missing workflow ID', 'Error', 'error');
                return;
            }

            const workflow = await (isNewWorkflow || isDuplicate
                ? createAgenticWorkflow(workflowPayload)
                : updateAgenticWorkflow(id as string, workflowPayload));

            if (workflow) {
                // Show success notification
                showNotification('Workflow saved successfully!', 'Success', 'success', () => {
                    return () => {
                        // Navigate to the specific workflow detail page if we have an ID, otherwise go to the workflow list
                        if (workflow.id) {
                            navigate(`/agentic-workflow/${workflow.id}`, { state: { workflow: workflowPayload } });
                        } else {
                            navigate('/agentic-workflow');
                        }
                    };
                });
            }
        } catch (error: unknown) {
            console.error('Error saving workflow:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            showNotification(`Error saving workflow: ${errorMessage}`, 'Error', 'error');
        }
    };

    const handleCancel = (): void => {
        // Show confirmation modal instead of navigating immediately
        setShowConfirmModal(true);
    };

    // Add a function to handle the actual navigation after confirmation
    const confirmCancel = (): void => {
        navigate('/agentic-workflow');
    };

    // Add a function to close the modal
    const closeConfirmModal = (): void => {
        setShowConfirmModal(false);
    };

    if (isLoadingEditAgenticWorkflow) {
        return <div>Loading workflow...</div>;
    }

    return (
        <FormProvider {...form}>
            <div className={styles.builderPage}>
                {/* Floating Sidebar */}
                <div className={styles.sidebar}>
                    <WorkflowConfigs isEdit={isEditWorkflow} />

                    <div style={{ borderTop: '1px solid #ccc', margin: '20px 0' }}></div>

                    <WorkflowActions />
                </div>
                <div className={styles.mainArea}>
                    <div className={styles.topBar}>
                        <div className={styles.flowNameDisplay}>{workflowTitle}</div>
                        <div className={styles.topButtons}>
                            <Button
                                className={styles.button}
                                onClick={form.handleSubmit(handleSave)}
                                disabled={isSavingWorkflow}
                            >
                                {isSavingWorkflow ? 'Saving...' : 'Save'}
                            </Button>
                            <Button className={`${styles.button} ${styles.exit}`} onClick={handleCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                    <div className={styles.canvasArea}>
                        <div className={styles.canvasPanelInner}>
                            <WorkflowCanvas
                                workflowDefinitionNodes={workflowData?.workflow_definition?.nodes || []}
                                counterRef={counterRef}
                                nodes={nodes}
                                setNodes={setNodes}
                                edges={edges}
                                setEdges={setEdges}
                                terminalDecisionNodeId={terminalDecisionNodeId}
                                reactFlowId='workflow-main'
                                isEditWorkflow={isEditWorkflow}
                            />
                        </div>
                        {!terminalDecisionNodeId && (
                            <div className={styles.terminalBanner}>
                                <p className={styles.terminalBannerText}>
                                    Add a dedicated decision node for terminal transition routing. It appears in the
                                    separate <strong>Terminal Decision Handler</strong> canvas below the main workflow.
                                </p>
                                <Button type='button' variant='primary' onClick={handleCreateTerminalDecisionNode}>
                                    Create Terminal Decision Handler
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <NotificationModal
                isOpen={notificationModalOpen}
                onClose={closeNotification}
                title={notificationTitle}
                message={notificationMessage}
                type={notificationType as any}
                buttons={[{ label: 'OK', onClick: closeNotification }]}
            />
            {/* Confirmation Modal */}
            <Modal
                isOpen={showConfirmModal}
                onClose={closeConfirmModal}
                title='Confirm Navigation'
                size='sm'
                showDefaultFooter={false}
                footer={
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <Button className='configCancelButton' onClick={closeConfirmModal}>
                            Stay
                        </Button>
                        <Button
                            className='configSaveButton'
                            style={{ backgroundColor: '#f44336' }}
                            onClick={confirmCancel}
                        >
                            Discard Changes
                        </Button>
                    </div>
                }
            >
                <p style={{ margin: '10px 0' }}>Are you sure you want to exit? Any unsaved changes will be lost.</p>
            </Modal>
        </FormProvider>
    );
};

export default AgenticWorkflowBuilder;
